<div class="request-header-card">

  <!-- ===== HEADER: icon/id + breadcrumb + status ===== -->
  <div class="request-header">

    <!-- Icon + request ID badge -->
    <div class="icon-column">
      <div class="category-icon" [ngStyle]="{
        'background-color': getMarkerBackgroundColor(request.category?.color),
        'border': '1px solid ' + request.category?.color
      }">
        <i [ngClass]="request.category.icon" class="subject-icon" [ngStyle]="{'color': request.category.color}"></i>
      </div>
      <span class="request-id-badge" *ngIf="request.requestId">{{ request.requestId }}</span>
    </div>

    <!-- Breadcrumb: Category > └ Subject > └ Department -->
    <div class="text-column">

      <!-- Level 1: Category -->
      <app-category-select
        class="request-category-main"
        [selectedCategory]="request.category"
        [categories]="allCategories"
        [requestId]="request._id"
        [readOnly]="!canEdit"
        (openSubjectMenu)="onOpenSubjectMenu($event)"
        (categoryAndSubjectChange)="onCategoryAndSubjectChange($event)"
        (revertChanges)="onRevertChanges()">
      </app-category-select>

      <!-- Level 2: └ Subject/SubCategory -->
      <div class="breadcrumb-row">
        <span class="breadcrumb-arrow">└</span>
        <app-subject-select
          #subjectSelect
          class="request-subject-inline"
          [subjects]="pendingCategoryChange ? tempCategory?.subCategories || [] : (request.category?.subCategories || [])"
          [selectedSubject]="request.subCategory"
          [requestId]="request._id"
          [variant]="'card'"
          [readOnly]="!canEdit"
          [pendingCategoryChange]="pendingCategoryChange"
          [parentCategory]="tempCategory"
          (subjectChange)="onSubCategoryChange($event)"
          (subjectChangeForCategory)="onSubCategoryChangeForCategory($event)"
          (menuClosed)="onSubjectMenuClosed()">
        </app-subject-select>
      </div>

      <!-- Level 3: └ Department (tooltip only) -->
      <div class="breadcrumb-row department-row"
           *ngIf="request.department"
           [matTooltip]="'request_v2.info.department_auto' | translate"
           matTooltipPosition="left">
        <span class="breadcrumb-arrow">└</span>
        <span class="department-name">{{ request.department?.name }}</span>
      </div>

    </div>

    <!-- Status (top-right) -->
    <div class="status-column">
      <app-status-select
        *ngIf="findStatus(request.state)"
        [currentStatus]="findStatus(request.state)"
        [statuses]="request.category?.statuses || []"
        [requestId]="request._id"
        [readOnly]="!canEdit"
        (statusChange)="onStatusChange($event)"
        (click)="$event.stopPropagation()">
      </app-status-select>
    </div>

  </div>

  <!-- ===== DESCRIPTION (inline) ===== -->
  <ng-container *ngIf="request.description || canEdit">
    <div class="description-label-row">
      <span class="section-label-upper">{{ 'request_v2.info.situation_description' | translate }}</span>
    </div>
    <div class="description-section">
      <!-- View mode -->
      <div class="description-text"
           *ngIf="!editingDescription"
           [class.is-editable]="canEdit"
           [attr.role]="canEdit ? 'button' : null"
           [attr.tabindex]="canEdit ? 0 : null"
           (click)="canEdit && startEditDescription()"
           (keydown.enter)="$event.preventDefault(); canEdit && startEditDescription()"
           (keydown.space)="$event.preventDefault(); canEdit && startEditDescription()">
        <span *ngIf="request.description">{{ request.description }}</span>
        <span class="description-placeholder" *ngIf="!request.description">
          {{ 'request_v2.info.add_description' | translate }}
        </span>
        <mat-icon *ngIf="canEdit" class="desc-edit-icon">edit</mat-icon>
      </div>
      <!-- Edit mode -->
      <div class="description-edit" *ngIf="editingDescription">
        <textarea #descTextarea
                  class="description-textarea"
                  [formControl]="descriptionCtrl"
                  [placeholder]="'request_v2.info.add_description' | translate"
                  rows="5"
                  (keydown.escape)="cancelEditDescription()"></textarea>
        <div class="desc-edit-actions">
          <button class="desc-icon-btn desc-cancel-btn" type="button" [attr.aria-label]="'common.cancel' | translate" (click)="cancelEditDescription()" [disabled]="savingDescription">
            <mat-icon>close</mat-icon>
          </button>
          <button class="desc-icon-btn desc-save-btn" type="button" [attr.aria-label]="'common.save' | translate" (click)="saveDescription()" [disabled]="!canSaveDescription">
            <mat-icon>check</mat-icon>
          </button>
        </div>
      </div>
    </div>
  </ng-container>

  <!-- ===== DEMANDEUR + EMPLACEMENT ===== -->
  <div class="info-sections">

    <!-- Demandeur -->
    <div class="info-section">
      <div class="section-label-upper">{{ 'request_v2.info.owner' | translate }}</div>
      <div class="requester-content" *ngIf="request.user">
        <div class="requester-row">
          <div class="user-avatar">
            <img *ngIf="request.user.picture" [src]="request.user.picture" alt="">
            <span *ngIf="!request.user.picture">{{ getInitials((request.user.firstName || '') + ' ' + (request.user.lastName || '')) }}</span>
          </div>
          <a [routerLink]="['/user-update', request.user._id]" target="_blank" class="user-name">
            {{ request.user.firstName }} {{ request.user.lastName }}
          </a>
        </div>
        <div class="contact-rows">
          <a *ngIf="requesterPhone" class="contact-row" href="tel:{{ requesterPhone }}">
            <mat-icon class="contact-icon">phone</mat-icon>
            <span>{{ requesterPhone }}</span>
          </a>
          <a *ngIf="request?.user?.email" class="contact-row" href="mailto:{{ request.user.email }}">
            <mat-icon class="contact-icon">email</mat-icon>
            <span>{{ request.user.email }}</span>
          </a>
        </div>
      </div>
      <span class="no-value" *ngIf="!request.user">{{ 'request_v2.info.no_owner' | translate }}</span>
    </div>

    <!-- Emplacement -->
    <div class="info-section">
      <div class="section-label-upper">{{ 'request_v2.info.location' | translate }}</div>
      <div class="address-content">
        <div class="address-icon-row" *ngIf="formatAddressLine1(request.location) || formatAddressLine2(request.location)">
          <mat-icon class="address-icon">place</mat-icon>
          <div class="address-lines">
            <span *ngIf="formatAddressLine1(request.location)" class="address-line">{{ formatAddressLine1(request.location) }}</span>
            <span *ngIf="formatAddressLine2(request.location)" class="address-line address-line-city">{{ formatAddressLine2(request.location) }}</span>
          </div>
        </div>
        <div class="entire-street-chip" *ngIf="request?.location?.entireStreet">
          <mat-icon class="warning-icon">warning_amber</mat-icon>
          <span>{{ 'request_v2.info.entire_street_affected' | translate }}</span>
        </div>
        <button *ngIf="hasValidLocation()" type="button" class="map-toggle-btn" (click)="toggleMap()">
          {{ isMapOpen ? ('request_v2.info.hide_map' | translate) : ('request_v2.info.view_map' | translate) }}
          <mat-icon class="map-toggle-icon">{{ isMapOpen ? 'expand_less' : 'expand_more' }}</mat-icon>
        </button>
      </div>
    </div>

  </div>

  <!-- ===== MAP ACCORDION ===== -->
  <div class="map-container" *ngIf="isMapOpen">
    <link
      rel="stylesheet"
      href="https://unpkg.com/leaflet@1.6.0/dist/leaflet.css"
      integrity="sha512-xwE/Az9zrjBIphAcBb3F6JVqxf46+CDLwfLMHloNu6KEQCAWi6HcDUbeOfBIptF7tcCzusKFjFw2yuvEpDL9wQ=="
      crossorigin=""
    />
    <div id="request-detail-map"></div>
  </div>

  <!-- ===== PIÈCES JOINTES (projected from parent) ===== -->
  <ng-content></ng-content>

</div>
